import { useAuth } from '@/modules/context/AuthContext'
import { DaycareRights } from '@/types/Context'
import { Role } from '@/types/Role'
import { useMemo, useState } from 'react'
import { DaycareType } from '@/types/Daycare.ts'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom'

export interface PlanningRights {
    canReadContracts: boolean
    canWriteContracts: boolean
    canReadOccasionalSlot: boolean
    canWriteOccasionalSlot: boolean
    canReadStaffWorks: boolean
    canWriteStaffWorks: boolean
    canReadStaffRights: boolean
    canReadParamsDaycare: boolean

    canViewGlobalTab: boolean
    canExportCalendar: boolean

    isRPEOrLAEP: boolean

    isParent: boolean
    isStaff: boolean
    isCald: boolean
    isTrustee: boolean
}

export function usePlanningRights(): PlanningRights {
    const { user } = useAuth()

    const rights: DaycareRights | undefined = user?.currentDaycareRights
    const role: Role | undefined = user?.role

    const canReadContracts: boolean = !!rights?.canReadContracts
    const canWriteContracts: boolean = !!rights?.canWriteContracts
    const canReadOccasionalSlot: boolean = !!rights?.canReadOccasionalSlot
    const canWriteOccasionalSlot: boolean = !!rights?.canWriteOccasionalSlot
    const canReadStaffWorks: boolean = !!rights?.canReadStaffWorks
    const canWriteStaffWorks: boolean = !!rights?.canWriteStaffWorks
    const canReadStaffRights: boolean = !!rights?.canReadStaffRights
    const canReadParamsDaycare: boolean = !!rights?.canReadParamsDaycare

    const isRPEOrLAEP: boolean =
        user?.currentDaycare?.type === DaycareType.rpe || user?.currentDaycare?.type === DaycareType.laep

    const isParent: boolean = role === Role.parent
    const isStaff: boolean = role === Role.staff
    const isCald: boolean = role === Role.cald
    const isTrustee: boolean = role === Role.trustee

    return {
        canReadContracts,
        canWriteContracts,
        canReadOccasionalSlot,
        canWriteOccasionalSlot,
        canReadStaffWorks,
        canWriteStaffWorks,
        canReadStaffRights,
        canReadParamsDaycare,
        canViewGlobalTab: canReadContracts && canReadOccasionalSlot && canReadStaffWorks,
        canExportCalendar: canReadOccasionalSlot || canReadContracts,
        isRPEOrLAEP,
        isParent,
        isStaff,
        isCald,
        isTrustee
    }
}

export enum PlanningTabId {
    PARENT_TAB = 'parent',
    REGULAR_TAB = 'regular',
    OCCASIONAL_TAB = 'occasional',
    STAFF_TAB = 'staff',
    GLOBAL_TAB = 'global'
}

export interface PlanningTab {
    id: PlanningTabId
    label: string
}

export interface PlanningTabsState {
    tabs: PlanningTab[]
    activeTabId: PlanningTabId
    activeIndex: number
    isRegularTab: boolean
    isOccasionalTab: boolean
    isStaffTab: boolean
    isGlobalTab: boolean
    isParentTab: boolean
    setActiveTabId: (id: PlanningTabId) => void
    handleTabChange: (_: unknown, index: number) => void
}

export function usePlanningTabs(t: (key: string) => string, initialTabIndex: string | null): PlanningTabsState {
    const rights: PlanningRights = usePlanningRights()
    const location: Location = useLocation()
    const navigate: NavigateFunction = useNavigate()

    const tabs: PlanningTab[] = useMemo((): PlanningTab[] => {
        const result: PlanningTab[] = []
        if (rights.canReadContracts) result.push({ id: PlanningTabId.REGULAR_TAB, label: t('regular') })
        if (rights.canReadOccasionalSlot)
            result.push({
                id: PlanningTabId.OCCASIONAL_TAB,
                label: rights.isRPEOrLAEP ? t('animation') : t('occasional')
            })
        if (rights.canReadStaffRights || rights.isStaff) result.push({ id: PlanningTabId.STAFF_TAB, label: t('staff') })
        if (result.length === 3 && rights.canViewGlobalTab)
            result.push({ id: PlanningTabId.GLOBAL_TAB, label: t('global') })
        return result
    }, [rights, t])

    const getDefaultTabId: () => PlanningTabId = (): PlanningTabId => {
        if (rights.isParent) return PlanningTabId.PARENT_TAB

        if (initialTabIndex !== null && tabs.find((tab: PlanningTab): boolean => tab.id === initialTabIndex)) {
            return initialTabIndex as PlanningTabId
        }

        if (rights.canReadOccasionalSlot) return PlanningTabId.OCCASIONAL_TAB
        if (rights.canReadContracts) return PlanningTabId.REGULAR_TAB
        if (rights.canReadStaffRights || rights.isStaff) return PlanningTabId.STAFF_TAB

        return PlanningTabId.OCCASIONAL_TAB
    }

    const [activeTabId, setActiveTabId] = useState<PlanningTabId>(getDefaultTabId())

    const activeIndex: number = tabs.findIndex((tab: PlanningTab): boolean => tab.id === activeTabId)
    const handleTabChange: (_: unknown, index: number) => void = (_: unknown, index: number): void => {
        const tabId: PlanningTabId = tabs[index].id
        setActiveTabId(tabId)

        const params: URLSearchParams = new URLSearchParams(location.search)
        params.set('tab', tabId)
        navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    }

    return {
        tabs,
        activeTabId,
        activeIndex,
        isRegularTab: activeTabId === PlanningTabId.REGULAR_TAB,
        isOccasionalTab: activeTabId === PlanningTabId.OCCASIONAL_TAB,
        isStaffTab: activeTabId === PlanningTabId.STAFF_TAB,
        isGlobalTab: activeTabId === PlanningTabId.GLOBAL_TAB,
        isParentTab: activeTabId === PlanningTabId.PARENT_TAB,
        setActiveTabId,
        handleTabChange
    }
}
